import React from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { ChevronRight, ChevronDown } from 'lucide-react';
import { TableColumn } from '../../agent-performance/tableConfig';
import { AgentMetricsTableCell } from './AgentMetricsTableCell';
import { AgentMetricsData } from '../../agent-performance/agentTableTypes';
import { AgentMetricsExpandedRow } from './AgentMetricsExpandedRow';

interface AgentMetricsTableRowProps {
  data: AgentMetricsData;
  columns: TableColumn[];
  index: number;
  onToggleExpansion?: (agentId: number) => void;
  isSticky?: boolean;
  onCellClick?: (
    columnKey: string,
    handledCallIds: string[],
    transferredCallIds: string[],
    callIdsSLA?: string[],
    totalSLACalls?: number,
    agentName?: string,
    queueName?: string
  ) => void;
}

export const AgentMetricsTableRow: React.FC<AgentMetricsTableRowProps> = ({
  data,
  columns,
  index,
  onToggleExpansion,
  isSticky = false,
  onCellClick,
}) => {
  const handleToggleExpansion = () => {
    if (data.id && onToggleExpansion) {
      onToggleExpansion(data.id);
    }
  };

  return (
    <>
        <div
          className={cn(
            'table-row transition-colors border-b border-gray-300 dark:border-white',
            index % 2 === 0 ? 'bg-white dark:bg-[#0C3451]' : 'bg-gray-50 dark:bg-[#3C5C73]'
          )}
          style={isSticky && data.isExpanded ? { 
            position: 'sticky', 
            top: '45px', 
            zIndex: 10 
          } : {}}
        >
          {columns.map((col, colIndex) => (
            <div
              key={col.key}
              className={cn(
                'table-cell h-[45px] px-2 text-xs text-teamscx-primary dark:text-white border-gray-300 dark:border-white transition-colors border-b',
                colIndex === 0 && ''
              )}
              style={{
                minWidth: colIndex === 0 ? '200px' : col.minWidth,
                maxWidth: colIndex === 0 ? '265px' : col.maxWidth,
                width: colIndex === 0 ? '240px' : `calc((100% - 240px) / ${columns.length - 1})`,
                display: colIndex === 0 ? 'flex' : '',
                alignItems: colIndex === 0 ? 'center' : '',
              }}
            >
              {colIndex === 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 hover:bg-primary/90 hover:text-white transition-all duration-200 flex items-center justify-center"
                  onClick={handleToggleExpansion}
                  aria-label={data.isExpanded ? 'Collapse row' : 'Expand row'}
                >
                  {data.isExpanded ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </Button>
              )}
              <AgentMetricsTableCell
                column={col}
                value={col.key === 'details' ? undefined : data[col.key as keyof Omit<AgentMetricsData, 'details'>]}
                rowData={data}
                isExpanded={data.isExpanded}
                isLoading={data.isMetricsLoading}
              />
            </div>
          ))}
        </div>
        {data.isExpanded && data.id && (
          <AgentMetricsExpandedRow
            details={data.details || []}
            backgroundColor={index % 2 !== 0 ? 'bg-gray-50 dark:bg-[#3C5C73]' : 'bg-white dark:bg-[#0C3451]'}
            isLoading={data.isMetricsLoading}
            agentName={data.displayName}
            onCellClick={onCellClick}
          />
        )}
    </>
  );
};
